import React, { useState, useEffect, useMemo } from 'react';
import { makeStyles } from '@material-ui/core/styles';

import Paper from '@material-ui/core/Paper';
import Typography from '@material-ui/core/Typography';
import TextField from '@material-ui/core/TextField';
import MenuItem from '@material-ui/core/MenuItem';
import Button from '@material-ui/core/Button';
import Grid from '@material-ui/core/Grid';
import Tooltip from '@material-ui/core/Tooltip';
import IconButton from '@material-ui/core/IconButton';
import Fab from '@material-ui/core/Fab';
import AddIcon from '@material-ui/icons/Add';

import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableHead from '@material-ui/core/TableHead';
import TableRow from '@material-ui/core/TableRow';

import RefreshIcon from '@material-ui/icons/Refresh'

import Loading from 'app/components/loaders/Loading';
import SendNotificationDialog from 'app/components/dialogs/SendNotificationDialog';

import { paper } from 'app/styles/core';
import { container } from 'app/styles/new-layout';

import useApi from 'app/context/useApi';

const useStyles = makeStyles(theme => ({
    container: {
        ...container(theme),
        padding: theme.spacing(3)
    },
    paper: {
        ...paper(theme),
        marginBottom: theme.spacing(3),
        position: 'relative'
    },
    extendedIcon: {
        marginRight: theme.spacing(1),

        [theme.breakpoints.down('sm')]: {
            marginRight: 0,
        }
    },
    marginBottom: {
        marginBottom: theme.spacing(1)
    },
    table: {
        width: '100%'
    },
    rotate: {
        animation: 'spin 1s infinite linear'
    },
    noRotate: {

    },
    recentSelect: {
        marginRight: theme.spacing(3)
    },
    tcSubject: {
        minWidth: 40,
        maxWidth: 120,
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap'
    },
    tcMessage: {
        minWidth: 40,
        maxWidth: 160,
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap'
    },
    tc: {
        minWidth: 40,
        maxWidth: 90,
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap'
    },
    empty: {
        paddingTop: theme.spacing(5),
        paddingBottom: theme.spacing(5),
        paddingLeft: theme.spacing(3),
        paddingRight: theme.spacing(3)
    },
}));

export default function Notifications (props) {

    const classes = useStyles();
    const api = useApi();

    const [ notifications, setNotifications ] = useState([]);
    const [ recent, setRecent ] = useState(10);
    const [ sendNotificationDialogOpen, setSendNotificationDialogOpen ] = useState(false);

    const [ notificationsLoading, setNotificationsLoading ] = useState(false);

    // Getter Functions ----------------------------------------

    const getNotifications = () => {
        setNotificationsLoading(true);

        api.get(`/admin/notification/` + recent)
            .then(res => setNotifications(res.notifications))
            .finally(() => setNotificationsLoading(false))

    };

    useEffect(() => {
        getNotifications();
    }, [recent]);

    const handleRecent = event => setRecent(event.target.value);

    // Send Notification Dialog Functions ------------------------------------------

    const openSendNotificationDialog = post => {
        setSendNotificationDialogOpen(true);
    }

    const closeSendNotificationDialog = () => {
        setSendNotificationDialogOpen(false);
    }

    const closeSendNotificationDialogAndRefresh = () => {
        closeSendNotificationDialog();
        getNotifications();
    }

    return (
        <div className={classes.container}>
            <Paper className={classes.paper}>
                <Grid container
                    alignItems="center"
                    justify="space-between"
                    spacing={3}
                    className={classes.marginBottom}
                    >
                    <Grid item>
                        <Typography variant="h6" component="h2">
                            Notifications
                        </Typography>
                    </Grid>
                    <Grid item>
                        <Fab
                            variant="extended"
                            onClick={openSendNotificationDialog}
                            color="primary"
                            aria-label="Send Notification"
                            >
                            <AddIcon className={classes.extendedIcon} />
                            Send Notification
                        </Fab>
                    </Grid>
                </Grid>
                <Typography color="textSecondary">
                    Notifications can be sent to all users or select groups of users. Based on your clubs subscription, several delivery options are available allowing your members and guests to stay updated.
                </Typography>
            </Paper>
            <Paper className={classes.paper}>
                <Loading isLoading={notificationsLoading}/>
                <Grid
                    container
                    justify="space-between"
                    spacing={3}
                    >
                    <Grid item>
                        <Typography variant="h6" component="h4">
                            Sent Notifications
                        </Typography>
                    </Grid>
                    <Grid item>
                        <TextField
                            id="recent-select"
                            select
                            value={recent}
                            onChange={handleRecent}
                            helperText={"Viewing previous " + recent + " notifications."}
                            variant="outlined"
                            className={classes.recentSelect}
                            >
                            <MenuItem value={10}>10</MenuItem>
                            <MenuItem value={50}>50</MenuItem>
                            <MenuItem value={100}>100</MenuItem>
                            <MenuItem value={"all"}>All Past Notifications</MenuItem>
                        </TextField>
                        <Tooltip title="Refresh Notifications">
                            <IconButton
                                aria-label="Refresh Notifications"
                                onClick={getNotifications}
                                >
                                <RefreshIcon
                                    className={notificationsLoading ? classes.rotate : classes.noRotate}
                                    fontSize="small"
                                />
                            </IconButton>
                        </Tooltip>
                    </Grid>
                </Grid>
                {notifications.length ? (
                    <Table className={classes.table} aria-label="Past Notifications">
                        <TableHead>
                            <TableRow>
                                <TableCell>Subject</TableCell>
                                <TableCell>Message</TableCell>
                                <TableCell>From</TableCell>
                                <TableCell>To</TableCell>
                                <TableCell>Action</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {notifications.map(n => (
                                <TableRow key={n.id}>
                                    <TableCell component="th" scope="row" className={classes.tcSubject}>
                                        {n.data.subject}
                                    </TableCell>
                                    <TableCell className={classes.tcMessage}>
                                        {n.data.message}
                                    </TableCell>
                                    <TableCell className={classes.tc}>
                                        {n.data.from}
                                    </TableCell>
                                    <TableCell className={classes.tc}>
                                        {n.data.to}
                                    </TableCell>
                                    <TableCell className={classes.tc}>
                                        {(n.data.action_text && n.data.action_text != "") &&
                                            <Button
                                                color="secondary"
                                                href={n.data.action_url}
                                                target="_blank"
                                                >
                                                {n.data.action_text}
                                            </Button>
                                        }
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                ) : (
                    <Typography
                        align="center"
                        color="textSecondary"
                        variant="subtitle2"
                        className={classes.empty}
                        >
                        No previous notifications have been sent. Send your first notification above.
                    </Typography>
                )}
            </Paper>
            <SendNotificationDialog
                open={sendNotificationDialogOpen}
                onClose={closeSendNotificationDialog}
                onSave={closeSendNotificationDialogAndRefresh}
            />
        </div>
    );
}
